/**
 * Helpers para manejar la lógica específica de slots de WhatsApp
 */

/**
 * Obtiene el valor de "Pertenece a" según el scope del slot
 * @param {Object} slot - Slot de WhatsApp de la API
 * @param {Function} Trans - Función de traducción
 * @param {Object} translations - Objeto de traducciones
 * @returns {string} Nombre del recurso al que pertenece
 */
export const getSlotBelongsTo = (slot, Trans, translations) => {
  if (slot.scope === 'branch') {
    return slot.branch?.name || '';
  }
  if (slot.scope === 'company') {
    return Trans(translations, 'company');
  }
  if (slot.scope === 'agent') {
    return slot.agent?.name || '';
  }
  return '';
};

/**
 * Obtiene el número de teléfono del slot según su scope
 * @param {Object} slot - Slot de WhatsApp de la API
 * @returns {string} Número de teléfono o '-'
 */
export const getSlotNumber = (slot) => {
  if (!slot) return '-';

  // Buscar el número según el scope del slot
  if (slot.scope === 'branch') {
    return slot.branch?.cellphone || '-';
  }
  if (slot.scope === 'company') {
    return slot.company?.cellphone || '-';
  }
  if (slot.scope === 'agent') {
    return slot.agent?.cellphone || '-';
  }

  // Para scope 'empty' u otros casos
  return '-';
};

/**
 * Obtiene el nombre de quien vinculó el slot
 * @param {Object} slot - Slot de WhatsApp de la API
 * @returns {string} Nombre de quien vinculó o string vacío
 */
export const getSlotLinkedBy = (slot) => {
  if (!slot.is_linked) return '';
  return slot.linked_by?.name || slot.agent?.name || '';
};

/**
 * Actualiza el cellphone del slot según su scope
 * @param {Object} slot - Slot a actualizar
 * @param {string} phoneNumber - Número de teléfono a asignar
 */
export const updateSlotPhone = (slot, phoneNumber) => {
  if (!slot || !phoneNumber) return;

  const scopeMap = {
    branch: 'branch',
    company: 'company',
    agent: 'agent',
  };

  const scopeKey = scopeMap[slot.scope];
  if (scopeKey) {
    if (!slot[scopeKey]) slot[scopeKey] = {};
    slot[scopeKey].cellphone = phoneNumber;
  }
};

/**
 * Calcula las banderas de disponibilidad de slots por tipo
 * @param {Object} assignmentStatus - Estado de asignaciones de la API
 * @returns {Object} Objeto con 4 banderas: global, company, branch, agent
 */
export const calculateAvailabilityFlags = (assignmentStatus) => {
  if (!assignmentStatus) {
    return {
      hasReachedMaxAssignments: false,
      hasReachedMaxCompany: false,
      hasReachedMaxBranch: false,
      hasReachedMaxAgent: false,
    };
  }

  const companyAvailable = assignmentStatus.company?.available ?? 0;
  const branchAvailable = assignmentStatus.branch?.available ?? 0;
  const agentAvailable = assignmentStatus.agent?.available ?? 0;

  return {
    // TODO: Integrar agentAvailable cuando se implemente la funcionalidad de agentes
    // Bandera global: company Y branch están en 0 (agente comentado temporalmente)
    hasReachedMaxAssignments: companyAvailable <= 0 && branchAvailable <= 0, // && agentAvailable <= 0,
    // Banderas individuales: cada tipo en 0
    hasReachedMaxCompany: companyAvailable <= 0,
    hasReachedMaxBranch: branchAvailable <= 0,
    hasReachedMaxAgent: agentAvailable <= 0, // Se mantiene para uso futuro
  };
};
